import React, { useState, useEffect, useRef } from 'react';
import { ImageIcon, Loader2 } from 'lucide-react';

/**
 * ProofThumbnail (iter114s.74)
 *
 * Lazy-loaded, on-demand <img> for the new artwork+dieline preview
 * that surfaces on Purchase Order History (customer) and
 * OrderManagementModal (admin). Hits
 * `GET /api/enhanced-orders/{orderId}/proof-thumbnail?item_id=…&size=…`
 * with the auth token attached, then renders the returned PNG.
 *
 * Why fetch-as-blob instead of `<img src={url}/>`:
 *   - The endpoint requires an `Authorization: Bearer …` header
 *     (orders are user-scoped). A plain <img> can't attach headers.
 *   - We render an authenticated blob URL into the <img>, then revoke
 *     it on unmount so we don't leak object URLs.
 *
 * Why IntersectionObserver lazy-load:
 *   - On long PO history tables the customer scrolls past most rows.
 *     Fetching every thumbnail upfront wastes bandwidth and stresses
 *     the artwork-rasteriser. Lazy fetch keeps the panel snappy.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProofThumbnail = ({
  orderId,
  itemId,
  size = 96,
  className = '',
  rounded = 'md',
  alt = 'Order proof preview',
  testId,
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const containerRef = useRef(null);

  useEffect(() => {
    if (!orderId) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;

    let observer;
    let cancelled = false;
    let createdUrl = null;

    const fetchThumbnail = async () => {
      try {
        setState('loading');
        const token = localStorage.getItem('access_token');
        const qs = new URLSearchParams();
        if (itemId) qs.set('item_id', itemId);
        qs.set('size', String(Math.min(800, Math.max(64, size * 2))));
        const res = await fetch(
          `${API_URL}/api/enhanced-orders/${orderId}/proof-thumbnail?${qs.toString()}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setState('ready');
      } catch (err) {
        if (!cancelled) setState('error');
      }
    };

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            fetchThumbnail();
            observer.disconnect();
          }
        },
        { rootMargin: '200px' },
      );
      observer.observe(node);
    } else {
      // Fallback for ancient browsers — fetch immediately.
      fetchThumbnail();
    }

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [orderId, itemId, size]);

  const radiusClass = {
    none: '',
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  }[rounded] ?? 'rounded-md';

  const base =
    `${radiusClass} bg-[#F4F7F2] border border-[#E5E7EB] overflow-hidden flex items-center justify-center ${className}`;

  if (state === 'ready' && blobUrl) {
    return (
      <img
        ref={containerRef}
        src={blobUrl}
        alt={alt}
        width={size}
        height={size}
        className={`${base} object-contain`}
        style={{ width: size, height: size }}
        data-testid={testId}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={base}
      style={{ width: size, height: size }}
      aria-label={alt}
      data-testid={testId}
    >
      {state === 'loading' ? (
        <Loader2 className="w-1/3 h-1/3 text-[#6FBF73] animate-spin" />
      ) : (
        <ImageIcon className="w-2/5 h-2/5 text-[#6FBF73]/40" />
      )}
    </div>
  );
};

export default ProofThumbnail;
